export const CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS = Object.freeze([
  "driver_license",
  "license",
  "insurance",
  "w9",
  "background_check",
  "certification",
] as const);

export const HISTORICAL_V1_CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS =
  Object.freeze([
    "license",
    "insurance",
    "w9",
    "background_check",
    "certification",
  ] as const);

export type CanonicalIndividualComplianceRequirementKey =
  typeof CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS[number];

export type CanonicalIndividualComplianceStatus =
  | "missing"
  | "pending"
  | "verified"
  | "rejected"
  | "expired";

export type CanonicalIndividualComplianceSelectionStatus =
  | "selected"
  | "no_candidate"
  | "ambiguous_candidate"
  | "unsupported";

export type CanonicalIndividualComplianceLifecycleState = "eligible";

export type CanonicalIndividualComplianceReviewState =
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

export type CanonicalIndividualComplianceFailureReason =
  | "evidence_missing"
  | "evidence_ambiguous"
  | "evidence_terminal"
  | "evidence_superseded"
  | "evidence_expired"
  | "expiration_unresolved"
  | "review_pending"
  | "review_not_approved"
  | "review_context_mismatch";

export type IndividualComplianceDocumentSource =
  | Readonly<{
      source: "canonical";
      evidenceId: string;
      canPreview: boolean;
    }>
  | Readonly<{
      source: "legacy_bridge";
      legacyDocumentId: string;
      canPreview: boolean;
    }>
  | Readonly<{
      source: "none";
      canPreview: false;
    }>;

export type CanonicalIndividualComplianceDocument = Readonly<{
  documentId: string;
  fileName: string;
  uploadedAt: string;
  issuedAt: string | null;
  expiresAt: string | null;
  note: string | null;
  rejectionReason: string | null;
}>;

export type CanonicalIndividualComplianceRequirement = Readonly<{
  requirementKey: CanonicalIndividualComplianceRequirementKey;
  label: string;
  description: string | null;
  required: boolean;
  sortOrder: number;
  status: CanonicalIndividualComplianceStatus;
  currentDocument: CanonicalIndividualComplianceDocument | null;
  documentSource: IndividualComplianceDocumentSource;
  uploadsAllowed: boolean;
  replacementAllowed: boolean;
  evidenceId: string | null;
  selectionStatus: CanonicalIndividualComplianceSelectionStatus;
  lifecycleState: CanonicalIndividualComplianceLifecycleState | null;
  evidenceEligible: boolean;
  reviewState: CanonicalIndividualComplianceReviewState;
  reviewApproved: boolean;
  operationallyVerified: boolean;
  latestDecisionId: string | null;
  failureReason: CanonicalIndividualComplianceFailureReason | null;
}>;

export type CanonicalIndividualComplianceRead = Readonly<{
  policyVersion: number;
  requirements: readonly CanonicalIndividualComplianceRequirement[];
}>;

export type ManagedCanonicalIndividualComplianceRead = Readonly<{
  userId: string;
  membershipStatus: "active";
  compliance: CanonicalIndividualComplianceRead;
}>;

export class CanonicalIndividualComplianceContractError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = "CanonicalIndividualComplianceContractError";
  }
}

const VALID_CANONICAL_INDIVIDUAL_COMPLIANCE_INVENTORIES = Object.freeze([
  HISTORICAL_V1_CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
  CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS,
] as const);

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_invalid_response",
    );
  }
  return value as Record<string, unknown>;
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
): boolean {
  const allowed = new Set(allowedKeys);
  return Object.keys(value).every((key) => allowed.has(key));
}

function requiredString(value: unknown): string {
  const normalized = typeof value === "string" ? value.trim() : "";
  if (!normalized) {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_invalid_response",
    );
  }
  return normalized;
}

export function isCanonicalIndividualComplianceRequirementKey(
  value: unknown,
): value is CanonicalIndividualComplianceRequirementKey {
  return (
    typeof value === "string"
    && CANONICAL_INDIVIDUAL_COMPLIANCE_REQUIREMENT_KEYS.includes(
      value as CanonicalIndividualComplianceRequirementKey,
    )
  );
}

export function assertCanonicalIndividualComplianceRequirementKey(
  value: unknown,
): CanonicalIndividualComplianceRequirementKey {
  if (!isCanonicalIndividualComplianceRequirementKey(value)) {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_requirement_unsupported",
    );
  }
  return value;
}

export function parseIndividualComplianceDocumentSource(
  value: unknown,
): IndividualComplianceDocumentSource {
  const source = asRecord(value);

  if (source.source === "canonical") {
    if (
      !hasOnlyKeys(source, ["source", "evidenceId", "canPreview"])
      || typeof source.canPreview !== "boolean"
    ) {
      throw new CanonicalIndividualComplianceContractError(
        "canonical_individual_compliance_source_invalid",
      );
    }
    return Object.freeze({
      source: "canonical",
      evidenceId: requiredString(source.evidenceId),
      canPreview: source.canPreview,
    });
  }

  if (source.source === "legacy_bridge") {
    if (
      !hasOnlyKeys(source, ["source", "legacyDocumentId", "canPreview"])
      || typeof source.canPreview !== "boolean"
    ) {
      throw new CanonicalIndividualComplianceContractError(
        "canonical_individual_compliance_source_invalid",
      );
    }
    return Object.freeze({
      source: "legacy_bridge",
      legacyDocumentId: requiredString(source.legacyDocumentId),
      canPreview: source.canPreview,
    });
  }

  if (
    source.source === "none"
    && source.canPreview === false
    && hasOnlyKeys(source, ["source", "canPreview"])
  ) {
    return Object.freeze({ source: "none", canPreview: false });
  }

  throw new CanonicalIndividualComplianceContractError(
    "canonical_individual_compliance_source_invalid",
  );
}

function parseCurrentDocument(
  value: unknown,
): Readonly<CanonicalIndividualComplianceDocument & { documentId: string }> | null {
  if (value === null) return null;
  const document = asRecord(value);
  return Object.freeze({
    documentId: requiredString(document.documentId),
    fileName: requiredString(document.fileName),
    uploadedAt: requiredString(document.uploadedAt),
    issuedAt:
      document.issuedAt === null ? null : requiredString(document.issuedAt),
    expiresAt:
      document.expiresAt === null ? null : requiredString(document.expiresAt),
    note:
      document.note === null ? null : requiredString(document.note),
    rejectionReason:
      document.rejectionReason === null
        ? null
        : requiredString(document.rejectionReason),
  });
}

function nullableString(value: unknown): string | null {
  return value === null ? null : requiredString(value);
}

function requireBoolean(
  value: unknown,
): boolean {
  if (typeof value !== "boolean") {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_invalid_response",
    );
  }
  return value;
}

function requireSelectionStatus(
  value: unknown,
): CanonicalIndividualComplianceSelectionStatus {
  const status = String(value ?? "") as CanonicalIndividualComplianceSelectionStatus;
  if (!["selected", "no_candidate", "ambiguous_candidate", "unsupported"].includes(status)) {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_invalid_response",
    );
  }
  return status;
}

function requireLifecycleState(
  value: unknown,
): CanonicalIndividualComplianceLifecycleState | null {
  if (value === null) return null;
  if (value === "eligible") return value;
  throw new CanonicalIndividualComplianceContractError(
    "canonical_individual_compliance_invalid_response",
  );
}

function requireReviewState(
  value: unknown,
): CanonicalIndividualComplianceReviewState {
  const status = String(value ?? "") as CanonicalIndividualComplianceReviewState;
  if (!["pending", "approved", "rejected", "changes_requested"].includes(status)) {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_invalid_response",
    );
  }
  return status;
}

function requireFailureReason(
  value: unknown,
): CanonicalIndividualComplianceFailureReason | null {
  if (value === null) return null;
  const reason = String(value ?? "") as CanonicalIndividualComplianceFailureReason;
  if (
    ![
      "evidence_missing",
      "evidence_ambiguous",
      "evidence_terminal",
      "evidence_superseded",
      "evidence_expired",
      "expiration_unresolved",
      "review_pending",
      "review_not_approved",
      "review_context_mismatch",
    ].includes(reason)
  ) {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_invalid_response",
    );
  }
  return reason;
}

function normalizeRequirement(
  value: unknown,
): CanonicalIndividualComplianceRequirement {
  const row = asRecord(value);
  const requirementKey = assertCanonicalIndividualComplianceRequirementKey(
    row.requirementKey,
  );
  const status = String(row.status ?? "") as CanonicalIndividualComplianceStatus;
  if (
    !["missing", "pending", "verified", "rejected", "expired"].includes(status)
    || !Number.isInteger(row.sortOrder)
  ) {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_invalid_response",
    );
  }

  const currentDocument = parseCurrentDocument(row.currentDocument);
  const source = String(row.source ?? "");
  const canPreview = requireBoolean(row.canPreview);
  const uploadsAllowed = requireBoolean(row.uploadsAllowed);
  const replacementAllowed = requireBoolean(row.replacementAllowed);
  const evidenceId = nullableString(row.evidenceId);
  const selectionStatus = requireSelectionStatus(row.selectionStatus);
  const lifecycleState = requireLifecycleState(row.lifecycleState);
  const evidenceEligible = requireBoolean(row.evidenceEligible);
  const reviewState = requireReviewState(row.reviewState);
  const reviewApproved = requireBoolean(row.reviewApproved);
  const operationallyVerified = requireBoolean(row.operationallyVerified);
  const latestDecisionId = nullableString(row.latestDecisionId);
  const failureReason = requireFailureReason(row.failureReason);

  let documentSource: IndividualComplianceDocumentSource;
  if (source === "canonical") {
    if (
      !currentDocument
      || !evidenceId
      || evidenceId !== currentDocument.documentId
      || uploadsAllowed
      || replacementAllowed
      || (status !== "pending" && status !== "expired")
    ) {
      throw new CanonicalIndividualComplianceContractError(
        "canonical_individual_compliance_capabilities_invalid",
      );
    }
    documentSource = Object.freeze({
      source: "canonical",
      evidenceId,
      canPreview,
    });
  } else if (source === "legacy_bridge") {
    if (
      !currentDocument
      || evidenceId !== null
      || !canPreview
      || !uploadsAllowed
      || !replacementAllowed
    ) {
      throw new CanonicalIndividualComplianceContractError(
        "canonical_individual_compliance_capabilities_invalid",
      );
    }
    documentSource = Object.freeze({
      source: "legacy_bridge",
      legacyDocumentId: currentDocument.documentId,
      canPreview: true,
    });
  } else if (source === "none") {
    if (
      currentDocument !== null
      || evidenceId !== null
      || canPreview
      || !uploadsAllowed
      || replacementAllowed
      || status !== "missing"
    ) {
      throw new CanonicalIndividualComplianceContractError(
        "canonical_individual_compliance_capabilities_invalid",
      );
    }
    documentSource = Object.freeze({ source: "none", canPreview: false });
  } else {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_source_invalid",
    );
  }

  return Object.freeze({
    requirementKey,
    label: String(row.label ?? ""),
    description:
      row.description === null ? null : String(row.description ?? ""),
    required: requireBoolean(row.required),
    sortOrder: Number(row.sortOrder),
    status,
    currentDocument: currentDocument
      ? Object.freeze({
          documentId: currentDocument.documentId,
          fileName: currentDocument.fileName,
          uploadedAt: currentDocument.uploadedAt,
          issuedAt: currentDocument.issuedAt,
          expiresAt: currentDocument.expiresAt,
          note: currentDocument.note,
          rejectionReason: currentDocument.rejectionReason,
        })
      : null,
    documentSource,
    uploadsAllowed,
    replacementAllowed,
    evidenceId,
    selectionStatus,
    lifecycleState,
    evidenceEligible,
    reviewState,
    reviewApproved,
    operationallyVerified,
    latestDecisionId,
    failureReason,
  });
}

export function parseCanonicalIndividualComplianceRead(
  value: unknown,
): CanonicalIndividualComplianceRead {
  const payload = asRecord(value);
  const requirements = Array.isArray(payload.requirements)
    ? payload.requirements
      .map(normalizeRequirement)
      .sort((left, right) => left.sortOrder - right.sortOrder)
    : [];

  const parsedInventory = requirements.map(
    (requirement) => requirement.requirementKey,
  );
  const matchesKnownInventory =
    VALID_CANONICAL_INDIVIDUAL_COMPLIANCE_INVENTORIES.some(
      (inventory) =>
        inventory.length === parsedInventory.length
        && inventory.every((key, index) => key === parsedInventory[index]),
    );

  if (!Number.isInteger(payload.policyVersion) || !matchesKnownInventory) {
    throw new CanonicalIndividualComplianceContractError(
      "canonical_individual_compliance_policy_inventory_invalid",
    );
  }

  return Object.freeze({
    policyVersion: Number(payload.policyVersion),
    requirements: Object.freeze(requirements),
  });
}
